import { useCallback, useRef, useState } from 'react'

import clsx from 'clsx'
import { rem } from 'polished'
import styled from 'styled-components'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'LuxKit/Button/TextButton'
import LineArrowRightIcon from 'LuxKit/Icons/line/LineArrowRightIcon'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import { INTL_SHORT_DAY_NAME_DAY_MONTH } from 'constants/dateFormats'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import eases from 'styles/tools/eases'

import OfferDatePicker from '../DatePicker/OfferDatePicker'
import noop from 'lib/function/noop'
import { useIntl } from 'react-intl'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import type { AccommodationMobileDayCellSelectHandler } from 'components/AccommodationKit/AccommodationCalendar/AccommodationCalendar.types'
import moment from 'moment'

const CalendarDatePickerWrapper = styled.div`
  padding-top: ${rem(20)};
`

const FooterContents = styled.div`
  position: absolute;
  bottom: 0;
  inset-inline: 0;
  transform: translateY(100%);
  transition: transform 0.3s ${eases.expo.in};

  &.visible {
    position: unset;
    transform: translateY(0);
    transition: transform 0.3s ${eases.expo.out};
  }
`

interface Props {
  propertyTimezone?: string
  checkIn?: string
  checkOut?: string
  onBack?: () => void
  onClose?: () => void
}

function OfferSidebarDatePicker(props: Props) {
  const {
    propertyTimezone,
    checkIn,
    checkOut,
    onBack,
    onClose = noop,
  } = props

  const [dates, setDates] = useState<{ checkIn?: string, checkOut?: string }>({
    checkIn,
    checkOut,
  })

  const intl = useIntl()
  const dispatch = useOfferPageDispatch()
  const intlDateFormatter = useIntlDateFormatter()
  const modalContext = useModalElementContext()

  const hasDaysSelected = !!(dates.checkIn && dates.checkOut)

  const onResetDates = useCallback(() => {
    setDates({ checkIn: undefined, checkOut: undefined })
  }, [])

  const onApply = useCallback(() => {
    if (!(dates.checkIn && !dates.checkOut)) {
      // only update state if it's not an 'invalid' (has check in, but not check out) selection
      dispatch({ type: OfferPageActions.setDates, checkIn: dates.checkIn, checkOut: dates.checkOut })
    }
    modalContext.resolve()
  }, [dates.checkIn, dates.checkOut, dispatch, modalContext])

  const onCloseModal = useCallback(() => {
    dispatch({ type: OfferPageActions.setDates, checkIn: dates.checkIn, checkOut: dates.checkOut })
    onClose()
    modalContext.resolve()
  }, [dispatch, dates.checkIn, dates.checkOut, onClose, modalContext])

  const scrollerRef = useRef<HTMLDivElement>(null)

  const onDatesChange = useCallback<AccommodationMobileDayCellSelectHandler>(day => {
    if (dates.checkIn && !dates.checkOut && moment(day.date).isAfter(dates.checkIn)) {
      setDates({ checkIn: dates.checkIn, checkOut: day.date })
    } else {
      setDates({ checkIn: day.date, checkOut: undefined })
    }
  }, [dates.checkIn, dates.checkOut])

  return (<>
    <ModalHeader
      title={intl.formatMessage({
        id: 'Y_jKRhYOWEmmIx2YXCaZ',
        defaultMessage: 'Select your dates',
        description: 'Heading title for the date picker modal on the offer page sidebar',
      })}
      onBackButtonClick={onBack}
      onCloseButtonClick={onCloseModal}
    >
    </ModalHeader>
    <ModalBody ref={scrollerRef}>
      <ModalContent>
        <CalendarDatePickerWrapper>
          <OfferDatePicker
            scrollerRef={scrollerRef}
            size="small"
            propertyTimezone={propertyTimezone}
            checkInDate={dates.checkIn}
            checkOutDate={dates.checkOut}
            onDatesChange={onDatesChange}
          />
        </CalendarDatePickerWrapper>
      </ModalContent>
    </ModalBody>
    <FooterContents className={clsx({ visible: !!hasDaysSelected })}>
      <ModalFooter>
        <VerticalSpacer gap={8}>
          {dates.checkIn && dates.checkOut && <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
            <BodyText variant="medium">
              <Group direction="horizontal" verticalAlign="center">
                <span>{intlDateFormatter(dates.checkIn, INTL_SHORT_DAY_NAME_DAY_MONTH)}</span>
                <LineArrowRightIcon size="XS" />
                <span>{intlDateFormatter(dates.checkOut, INTL_SHORT_DAY_NAME_DAY_MONTH)}</span>
              </Group>
            </BodyText>
            <TextButton kind="tertiary" horizontalOutdent="end" onClick={onResetDates}>
              {intl.formatMessage({
                id: '7SzKCvRhP6Tb2Jqq_d-W',
                defaultMessage: 'Reset',
                description: 'Button text to reset selected dates in sidebar date picker',
              })}
            </TextButton>
          </Group>}
          <Group direction="horizontal" horizontalAlign="space-between">
            <TextButton kind="primary" size="large" type="submit" fit="mobile-full-width" onClick={onApply}>
              {intl.formatMessage({
                id: 'byRjBo7yRMtPGjfs8taE',
                defaultMessage: 'Apply',
                description: 'Button text to apply selected dates in sidebar date picker',
              })}
            </TextButton>
          </Group>
        </VerticalSpacer>
      </ModalFooter>
    </FooterContents>
  </>)
}

export default withOfferPageState((state) => ({
  propertyTimezone: state.property?.location.timezone ?? state.offer?.property.timezone ?? state.bedbankOffer?.property.timezone,
  checkIn: state.checkInDate,
  checkOut: state.checkOutDate,
}), OfferSidebarDatePicker)
